/**
 * Copyright © 2025 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useState } from "react";
import { Box, Button, Typography, TextField } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import apiFetch from "./apiFetch";

function Logger() {
    const [startDate, setStartDate] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [logs, setLogs] = useState(null);

    // Handle Download prompt
    const handleDownload = async (event) => {
        event.preventDefault();
        if (!startDate) {
            setError("Enter the required date.");
            return;
        }

        setError("");
        try {
            const blob = await apiFetch(
                `/api/logs/generate-pdf?realmId=${sessionStorage.getItem('realmId')}&date=${startDate.format("YYYY-MM-DD")}`,
                { responseType: 'blob' }
            );

            if (blob === null) return; // Session expired, handled by apiFetch

            const fileHandle = await window.showSaveFilePicker({
                suggestedName: `logs_${startDate.format("YYYY-MM-DD")}.pdf`,
                types: [
                    {
                        description: 'PDF Files',
                        accept: { 'application/pdf': ['.pdf'] },
                    },
                ],
            });

            const writableStream = await fileHandle.createWritable();
            await writableStream.write(blob);
            await writableStream.close();
        } catch (err) {
            setError("An error occurred while fetching logs.");
            console.error(err);
        }
    };

    // Fetching logs from database
    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!startDate) {
            setError("Enter the required date.");
            return;
        }

        setError("");
        setLoading(true);

        try {
            const data = await apiFetch(
                `/api/logs?realmId=${sessionStorage.getItem('realmId')}&date=${startDate.format("YYYY-MM-DD")}`
            );

            if (data === null) return; // Session expired, handled by apiFetch

            if (JSON.stringify(data) === JSON.stringify([])) {
                setLogs("No logs available for this date.");
            } else {
                setLogs(data);
            }
        } catch (err) {
            setError("An error occurred while fetching logs.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const minDate = new Date(2022, 0, 1);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ alignItems: "center" }} gap={2} p={2}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", textAlign: "center" }}>
                    Logs
                </Typography>
            </Box>
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    width: "66%",
                    margin: "auto",
                    maxWidth: 800,
                    padding: 2,
                    boxShadow: 3,
                    borderRadius: 2,
                }}
            >
                <Typography variant="h6" sx={{ textAlign: "left" }}>
                    Date
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        gap: 2,
                        width: "100%",
                    }}
                >
                    <DatePicker
                        label="Start Date"
                        value={startDate}
                        shouldDisableDate={(date) => date < minDate}
                        slotProps={{ textField: { fullWidth: true } }}
                        onChange={(newValue) => {
                            setStartDate(newValue);
                            if (error) setError("");
                        }}
                        sx={{
                            width: "400px",
                        }}
                        renderInput={(params) => (
                            <TextField {...params} sx={{ flexGrow: 1, width: '80%' }} />
                        )}
                    />
                </Box>
                {error && (
                    <Typography color="error" sx={{ textAlign: "center" }}>
                        {error}
                    </Typography>
                )}
                <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{
                            width: "300px",
                        }}
                        disabled={loading}
                    >
                        {loading ? "Loading..." : "Get Logs"}
                    </Button>
                    <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        className="primary"
                        onClick={handleDownload}
                        sx={{
                            width: "300px",
                        }}
                    >
                        Download Logs as PDF
                    </Button>
                </Box>
            </Box>

            {logs || error ? (
                <Box
                    sx={{
                        marginTop: 2,
                        padding: 2,
                        boxShadow: 3,
                        borderRadius: 2,
                        border: "1px solid #ccc",
                        width: "66%",
                        margin: "auto",
                        maxHeight: "calc(100vh - 300px)",
                        overflowY: "auto",
                    }}
                >
                    <Typography variant="h6" sx={{ textAlign: "center" }}>
                        {error ? "Error" : "Logs"}
                    </Typography>
                    <pre>{error ? error : JSON.stringify(logs, null, 2)}</pre>
                </Box>
            ) : null}
        </LocalizationProvider>
    );
}

export default Logger;
